/**
 * Tiered Pricing System - Feature-complete cart-based discounts
 *
 * Features:
 * - Bundle product support via _tiered_pricing_count metafield
 * - Product tag exclusions (prevent_discount, set, Treatment)
 * - Discount code blacklist
 * - Gift item detection (_Gifted property)
 * - Quantity selector integration
 * - Event-driven architecture with PubSub
 *  * - Tiered pricing exception: elements with data-exception-tiers="true" use
 *   window.tieredPricing.exceptionTiers (products tagged with the exception tag)
 */

// Base class for tiered pricing components
class TieredPricingBase extends HTMLElement {
  constructor() {
    super();

    if (!window.tieredPricing) {
      console.warn('Tiered pricing not initialized');
      return;
    }

    this.config = window.tieredPricing;
    this.tiers = this.config.tiers || [];

    // Tiered pricing exception: use the exception tiers for tagged products
    if (this.dataset.exceptionTiers === 'true' && Array.isArray(this.config.exceptionTiers) && this.config.exceptionTiers.length > 0) {
      this.tiers = this.config.exceptionTiers;
    }

    // Exclusion settings from data attributes
    this.preventDiscount = this.dataset.preventDiscount === 'true';
  }


  /**
   * Check if tiered pricing should be disabled
   * Checks: preventDiscount flag, excluded discount codes
   */
  isDisabled() {
    // Check prevent discount flag
    if (this.preventDiscount) {
      return true;
    }

    // Check if excluded discount code is active
    if (this.config.excludedDiscountCodes && this.config.activeDiscountCode) {
      const activeCode = this.config.activeDiscountCode.toLowerCase();
      return this.config.excludedDiscountCodes.some(excludedCode => {
        const excluded = excludedCode.toLowerCase();
        // Support partial matching (e.g., "NQD" matches "NQD10")
        return activeCode.includes(excluded);
      });
    }

    return false;
  }

  /**
   * Calculate weighted cart count
   * Accounts for:
   * - Bundle products (_tiered_pricing_count metafield)
   * - Gift items (_Gifted property - excluded from count)
   * - Regular products (count as 1 each)
   *
   * @param {Array} cartItems - Cart items from Shopify
   * @param {Number} additionalQuantity - Quantity being added (for PDP preview)
   * @param {Number} additionalBundleCount - Bundle count for item being added
   * @returns {Number} Total weighted count
   */
  calculateWeightedCount(cartItems = [], additionalQuantity = 0, additionalBundleCount = 1) {
    let totalCount = 0;

    // Process existing cart items
    if (cartItems && Array.isArray(cartItems)) {
      cartItems.forEach(item => {
        // Skip gift items
        if (item.properties && item.properties._Gifted) {
          return;
        }

        // Get bundle count from item properties
        const bundleCount = item.properties && item.properties._tiered_pricing_count
          ? parseInt(item.properties._tiered_pricing_count)
          : 1;

        // Add weighted count: quantity × bundle multiplier
        totalCount += item.quantity * bundleCount;
      });
    }

    // Add the item being viewed/added (for PDP preview)
    if (additionalQuantity > 0) {
      totalCount += additionalQuantity * additionalBundleCount;
    }

    return totalCount;
  }

  /**
   * Get applicable discount for item count
   *
   * @param {Number} cartItemCount - Current cart count (weighted)
   * @param {Number} additionalQuantity - Quantity to add (optional, for PDP)
   * @param {Number} additionalBundleCount - Bundle multiplier for item being added
   * @returns {Object|null} Discount tier or null
   */
  getDiscount(cartItemCount, additionalQuantity = 1, additionalBundleCount = 1) {
    // Check if disabled by exclusion rules
    if (this.isDisabled()) {
      return null;
    }

    // Calculate total including potential addition
    const totalCount = cartItemCount + (additionalQuantity * additionalBundleCount);

    // Find highest applicable tier
    const applicableTiers = this.tiers.filter(tier => totalCount >= tier.threshold);

    if (applicableTiers.length === 0) return null;

    return applicableTiers.reduce((max, tier) =>
      tier.discount > max.discount ? tier : max
    );
  }
}

// Price component - shows discounted price
class TieredPricingPrice extends TieredPricingBase {
  constructor() {
    super();

    this.originalPrice = parseInt(this.dataset.price);
    this.bundleCount = parseInt(this.dataset.bundleCount) || 1;
    this.priceElement = this.querySelector('[data-tiered-price-sale]');
    this.regularPriceElement = this.querySelector('[data-tiered-price-regular]');

    // Check if in cart context
    this.inCartContext = this.isInCartContext();

    // Find main quantity input (sticky cart syncs to this one) - only for PDP
    // Priority: scoped search via closest() ensures correct input on pages with multiple products
    // (e.g., recommendations, quick view). Falls back to global query for edge cases where
    // the component isn't inside a form element.
    if (!this.inCartContext) {
      this.quantityInput = this.closest('form, product-form')?.querySelector('input[name="quantity"]')
        || document.querySelector('.product-form:not(.product__sticky .product-form) input[name="quantity"]');
    }

    this.init();

    // Only attach quantity listener for PDP (not cart)
    if (!this.inCartContext) {
      this.attachQuantityListener();
    }
  }

  /**
   * Check if this price element is inside cart drawer/notification
   * Cart prices should NOT be modified - return true to disable
   * Exception: upsell items are previewed before being added, so allow tiered pricing there
   */
  isInCartContext() {
    if (this.closest('.cart-upsell__slide')) return false;
    return this.closest('cart-notification') !== null ||
      this.closest('.cart-notification-product') !== null ||
      this.closest('#offcanvas__mini_cart') !== null ||
      this.closest('.cart-drawer') !== null ||
      this.closest('[id*="cart"]') !== null;
  }

  connectedCallback() {
    if (typeof PUB_SUB_EVENTS === 'undefined' || typeof subscribe === 'undefined') {
      return;
    }

    this.unsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.handleCartUpdate(event);
    });
  }

  disconnectedCallback() {
    if (this.unsubscriber) {
      this.unsubscriber();
    }
  }

  init() {
    // Don't modify anything in cart context
    if (this.inCartContext) {
      return;
    }

    // Use cached cart items if available
    const cartItems = window.tieredPricing.cartItems || [];
    this.updatePrice(cartItems, 1);
  }

  /**
   * Listen to quantity input changes for real-time preview
   */
  attachQuantityListener() {
    if (!this.quantityInput) {
      return;
    }

    ['input', 'change'].forEach(eventType => {
      this.quantityInput.addEventListener(eventType, () => {
        const quantity = parseInt(this.quantityInput.value) || 1;
        const cartItems = window.tieredPricing.cartItems || [];
        this.updatePrice(cartItems, quantity);
      });
    });
  }

  handleCartUpdate(event) {
    // Don't modify anything in cart context
    if (this.inCartContext) {
      return;
    }

    const cartData = event.cartData || event;

    if (!cartData || !cartData.items || !Array.isArray(cartData.items)) {
      return;
    }

    // Cache cart items for other components
    window.tieredPricing.cartItems = cartData.items;

    // Calculate weighted count
    const weightedCount = this.calculateWeightedCount(cartData.items);
    window.tieredPricing.itemCount = weightedCount;

    // Only update PDP preview
    this.updatePrice(cartData.items, 1);
  }

  updatePrice(cartItems, additionalQuantity = 1) {
    // Calculate current cart weighted count
    const cartCount = this.calculateWeightedCount(cartItems, 0, 0);

    // Get discount including the quantity being added
    const discount = this.getDiscount(cartCount, additionalQuantity, this.bundleCount);

    if (discount && this.priceElement && this.regularPriceElement) {
      const discountedPrice = this.originalPrice * (1 - discount.discount / 100);

      this.priceElement.innerHTML = formatPrice(discountedPrice, this.config.moneyFormat, this.config.locale);
      this.regularPriceElement.innerHTML = formatPrice(this.originalPrice, this.config.moneyFormat, this.config.locale);
      this.regularPriceElement.style.display = '';
    } else if (this.priceElement && this.regularPriceElement) {
      this.priceElement.innerHTML = formatPrice(this.originalPrice, this.config.moneyFormat, this.config.locale);
      this.regularPriceElement.style.display = 'none';
    }
  }
}

// Badge component - shows discount percentage
class TieredPricingBadge extends TieredPricingBase {
  constructor() {
    super();

    this.badge = this.querySelector('[data-tiered-badge]');
    this.bundleCount = parseInt(this.dataset.bundleCount) || 1;

    // Check if in cart context
    this.inCartContext = this.isInCartContext();

    // Find main quantity input (sticky cart syncs to this one) - only for PDP
    // Priority: scoped search via closest() ensures correct input on pages with multiple products
    // (e.g., recommendations, quick view). Falls back to global query for edge cases where
    // the component isn't inside a form element.
    if (!this.inCartContext) {
      this.quantityInput = this.closest('form, product-form')?.querySelector('input[name="quantity"]')
        || document.querySelector('.product-form:not(.product__sticky .product-form) input[name="quantity"]');
    }

    this.init();

    // Only attach quantity listener for PDP (not cart)
    if (!this.inCartContext) {
      this.attachQuantityListener();
    }
  }

  /**
   * Check if this badge element is inside cart drawer/notification
   * Cart badges should NOT be modified - return true to disable
   * Exception: upsell items are previewed before being added, so allow tiered pricing there
   */
  isInCartContext() {
    if (this.closest('.cart-upsell__slide')) return false;
    return this.closest('cart-notification') !== null ||
      this.closest('.cart-notification-product') !== null ||
      this.closest('#offcanvas__mini_cart') !== null ||
      this.closest('.cart-drawer') !== null ||
      this.closest('[id*="cart"]') !== null;
  }

  connectedCallback() {
    if (typeof PUB_SUB_EVENTS === 'undefined' || typeof subscribe === 'undefined') {
      return;
    }

    this.unsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.handleCartUpdate(event);
    });
  }

  disconnectedCallback() {
    if (this.unsubscriber) {
      this.unsubscriber();
    }
  }

  init() {
    // Don't modify anything in cart context
    if (this.inCartContext) {
      return;
    }

    // Skip calculation if static discount is set (bundles)
    if (this.dataset.staticDiscount) {
      this.classList.remove('hidden');
      return;
    }

    const cartItems = window.tieredPricing.cartItems || [];
    this.updateBadge(cartItems, 1);
  }

  /**
   * Listen to quantity input changes for real-time preview
   */
  attachQuantityListener() {
    // Skip if static discount is set (bundles)
    if (!this.quantityInput || this.dataset.staticDiscount) return;

    ['input', 'change'].forEach(eventType => {
      this.quantityInput.addEventListener(eventType, () => {
        const quantity = parseInt(this.quantityInput.value) || 1;
        const cartItems = window.tieredPricing.cartItems || [];
        this.updateBadge(cartItems, quantity);
      });
    });
  }

  handleCartUpdate(event) {
    // Don't modify anything in cart context
    if (this.inCartContext) {
      return;
    }

    // Skip calculation if static discount is set (bundles)
    if (this.dataset.staticDiscount) {
      return;
    }

    const cartData = event.cartData || event;

    if (!cartData || !cartData.items || !Array.isArray(cartData.items)) {
      return;
    }

    window.tieredPricing.cartItems = cartData.items;
    const weightedCount = this.calculateWeightedCount(cartData.items);
    window.tieredPricing.itemCount = weightedCount;

    // Only update PDP preview
    this.updateBadge(cartData.items, 1);
  }

  updateBadge(cartItems, additionalQuantity = 1) {
    // Calculate current cart weighted count
    const cartCount = this.calculateWeightedCount(cartItems, 0, 0);

    // Get discount including the quantity being added
    const discount = this.getDiscount(cartCount, additionalQuantity, this.bundleCount);

    if (discount && this.badge) {
      this.classList.remove('hidden');
      this.badge.innerHTML = `<span class="badge__discount">-${discount.discount}%</span><span class="badge__discount-separator">&nbsp;</span><span class="badge__label">${this.config.discountLabel}</span>`;
    } else {
      this.classList.add('hidden');
    }
  }
}

// Table component - highlights the active tier row
class TieredPricingTable extends TieredPricingBase {
  constructor() {
    super();

    this.tierRows = this.querySelectorAll('[data-threshold]');
    this.bundleCount = parseInt(this.dataset.bundleCount) || 1;

    this.quantityInput = document.querySelector('.product-form:not(.product__sticky .product-form) input[name="quantity"]');

    this.init();
    this.attachQuantityListener();
  }

  connectedCallback() {
    if (typeof PUB_SUB_EVENTS === 'undefined' || typeof subscribe === 'undefined') return;
    this.unsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const cartData = event.cartData || event;
      if (cartData?.items) {
        window.tieredPricing.cartItems = cartData.items;
        window.tieredPricing.itemCount = this.calculateWeightedCount(cartData.items);
      }
      this.updateActive();
    });
  }

  disconnectedCallback() {
    if (this.unsubscriber) this.unsubscriber();
  }

  init() {
    this.updateActive();
  }

  attachQuantityListener() {
    if (!this.quantityInput) return;
    ['input', 'change'].forEach(evt => {
      this.quantityInput.addEventListener(evt, () => this.updateActive());
    });
  }

  updateActive() {
    const cartItems = window.tieredPricing.cartItems || [];
    const cartCount = this.calculateWeightedCount(cartItems, 0, 0);
    const qty = this.quantityInput ? (parseInt(this.quantityInput.value) || 1) : 1;
    const totalCount = cartCount + (qty * this.bundleCount);

    // Find highest applicable threshold
    let activeThreshold = null;
    this.tiers.forEach(tier => {
      if (totalCount >= tier.threshold) {
        if (!activeThreshold || tier.threshold > activeThreshold) {
          activeThreshold = tier.threshold;
        }
      }
    });

    this.tierRows.forEach(row => {
      const threshold = parseInt(row.dataset.threshold);
      row.classList.toggle('volume-pricing-table__tier--active', threshold === activeThreshold);
    });
  }
}

// Register custom elements
if (!customElements.get('tiered-pricing-price')) {
  customElements.define('tiered-pricing-price', TieredPricingPrice);
}

if (!customElements.get('tiered-pricing-badge')) {
  customElements.define('tiered-pricing-badge', TieredPricingBadge);
}

if (!customElements.get('tiered-pricing-table')) {
  customElements.define('tiered-pricing-table', TieredPricingTable);
}
